import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Github, 
  User, 
  Lock, 
  Shield, 
  Link2, 
  Key,
  Users,
  HelpCircle,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

interface SettingsLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const settingsNav: NavItem[] = [
  { name: 'GitHub Integration', href: '/settings/github', icon: Github },
  { name: 'Account Settings', href: '/settings/account', icon: User },
  { name: 'Change Password', href: '/settings/password', icon: Lock },
  { name: 'Two-Factor Authentication', href: '/settings/2fa', icon: Shield },
  { name: 'Linked Accounts', href: '/settings/oauth', icon: Link2 },
  { name: 'SSH Keys', href: '/settings/ssh-keys', icon: Key, adminOnly: true },
  { name: 'Projects & Billing', href: '/settings/billing', icon: CreditCard },
  { name: 'Project Memberships', href: '/settings/memberships', icon: Users },
  // NOTE: API Access and Privacy & Data disabled for end users
  // { name: 'API Access', href: '/settings/api', icon: Zap },
  // { name: 'Privacy & Data', href: '/settings/legal', icon: FileWarning },
];

export function SettingsLayout({ children }: SettingsLayoutProps) {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <h2 className="text-xl font-bold mb-4">Settings</h2>
            <nav className="space-y-1">
              {settingsNav.map((item) => {
                if (item.adminOnly && !user?.is_admin) return null;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-gray-700 hover:bg-gray-100"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            
            <div className="mt-6">
              <Button variant="secondary" className="w-full" asChild>
                <a href="mailto:support@doploy.io">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Get help
                </a>
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 max-w-2xl">
            {children}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
